import React, { useEffect, useState, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Upload, FileText, ExternalLink, Trash2, FolderArchive } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const YEARS = [2024, 2025, 2026];
const CATS = [
  { id: "memo_internal", label: "Memo Internal", color: "bg-showty-red text-white" },
  { id: "memo_external", label: "Memo External", color: "bg-showty-navy text-white" },
  { id: "form", label: "Formulir", color: "bg-showty-gold/80 text-white" },
];
const catInfo = (id) => CATS.find((c) => c.id === id) || { label: id, color: "bg-muted" };

export default function ArchivePanel() {
  const [year, setYear] = useState(2026);
  const [cat, setCat] = useState("all");
  const [items, setItems] = useState([]);

  const load = useCallback(() => {
    const p = new URLSearchParams({ year: String(year) });
    if (cat !== "all") p.append("category", cat);
    api.get(`/archives?${p.toString()}`).then((r) => setItems(r.data)).catch(() => {});
  }, [year, cat]);
  useEffect(() => { load(); }, [load]);

  const open = async (a) => {
    try {
      const res = await api.get(`/archives/${a.id}/file`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch (e) { toast.error("Gagal membuka file"); }
  };
  const remove = async (id) => { try { await api.delete(`/archives/${id}`); load(); } catch (e) { toast.error("Gagal menghapus"); } };

  return (
    <div data-testid="archive-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h3 className="font-display text-2xl text-showty-navy flex items-center gap-2"><FolderArchive className="w-5 h-5 text-showty-red" /> Database Arsip</h3>
          <p className="text-sm text-muted-foreground">Upload file hard copy — otomatis terarsip berdasarkan tahun & tanggal (2024–2026).</p>
        </div>
        <UploadArchiveDialog onDone={load} defaultYear={year} />
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="inline-flex rounded-full border border-border bg-white p-1" data-testid="archive-year-filter">
          {YEARS.map((y) => (
            <button key={y} onClick={() => setYear(y)} data-testid={`archive-year-${y}`}
              className={`rounded-full px-4 py-1.5 text-sm transition-colors ${year === y ? "bg-showty-navy text-white" : "text-muted-foreground hover:text-showty-navy"}`}>{y}</button>
          ))}
        </div>
        <Select value={cat} onValueChange={setCat}>
          <SelectTrigger className="w-44 h-9" data-testid="archive-cat-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Kategori</SelectItem>
            {CATS.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-white/50 py-16 text-center text-sm text-muted-foreground" data-testid="archive-empty">
          <FolderArchive className="w-6 h-6 mx-auto mb-2 text-showty-red/50" /> Belum ada arsip untuk tahun {year}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="archive-list">
          {items.map((a) => {
            const ci = catInfo(a.category);
            return (
              <div key={a.id} className="rounded-2xl border border-border bg-white p-4 flex flex-col" data-testid={`archive-${a.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <span className={`text-[10px] uppercase tracking-wide font-semibold rounded-full px-2 py-0.5 ${ci.color}`}>{ci.label}</span>
                  <button onClick={() => remove(a.id)} className="text-muted-foreground hover:text-destructive" data-testid={`archive-delete-${a.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
                <div className="mt-3 flex items-start gap-2">
                  <FileText className="w-5 h-5 text-showty-red shrink-0 mt-0.5" />
                  <p className="font-medium text-showty-navy leading-snug">{a.title}</p>
                </div>
                <p className="text-xs text-muted-foreground mt-1">{a.date} · {a.original_filename}</p>
                <Button onClick={() => open(a)} variant="outline" size="sm" className="rounded-full mt-3 self-start" data-testid={`archive-open-${a.id}`}>
                  <ExternalLink className="w-3.5 h-3.5 mr-1" /> Buka File
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const UploadArchiveDialog = ({ onDone, defaultYear }) => {
  const [open, setOpen] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const [f, setF] = useState({ title: "", category: "memo_internal", date: `${defaultYear}-01-01` });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!f.title.trim()) return toast.error("Judul wajib diisi");
    if (!file) return toast.error("Pilih file hard copy dulu");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("title", f.title);
      fd.append("category", f.category);
      fd.append("date", f.date);
      fd.append("file", file);
      await api.post("/archives", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("File terarsip");
      setOpen(false);
      setF({ title: "", category: "memo_internal", date: today });
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal upload arsip"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="rounded-full bg-showty-red hover:bg-[#9e1f24] text-white" data-testid="archive-upload-btn"><Upload className="w-4 h-4 mr-1" /> Upload Arsip</Button></DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="archive-upload-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl text-showty-navy">Upload File Arsip</DialogTitle></DialogHeader>
        <div className="space-y-3 py-1">
          <div><Label>Judul dokumen</Label><Input className="mt-1" value={f.title} onChange={(e) => set("title", e.target.value)} data-testid="archive-title" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Kategori</Label>
              <Select value={f.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger className="mt-1" data-testid="archive-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATS.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Tanggal</Label><Input type="date" min="2024-01-01" max="2026-12-31" className="mt-1" value={f.date} onChange={(e) => set("date", e.target.value)} data-testid="archive-date" /></div>
          </div>
          <div><Label>File hard copy (PDF / gambar / dokumen)</Label>
            <Input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx" className="mt-1" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="archive-file" />
            {file && <p className="mt-1 text-xs text-muted-foreground">{file.name}</p>}
          </div>
        </div>
        <DialogFooter><Button onClick={save} disabled={busy} className="rounded-full bg-showty-red hover:bg-[#9e1f24] text-white w-full" data-testid="archive-save">{busy ? "Mengunggah…" : "Simpan ke Arsip"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
